/** Marke, Themen und Ortsstruktur für die regionalen Landingpages. */

export const BRAND = {
  name: "Immobilienwert Online Zürich",
  company: "PartNET AG",
  domain: "immobilienwertonline.ch",
  contact: "Mohamed Boulad",
  phone: "+41 44 512 85 12",
  address: "PartNET AG · Fracht West 1, 8058 Zürich-Flughafen",
  street: "Fracht West 1",
  postalCode: "8058",
  city: "Zürich-Flughafen",
  experience: "über 20 Jahre",
} as const;

export type Topic = {
  slug: string;
  label: string;
  /** true = allgemeiner Standort-Cluster ohne Themen-Prefix in der URL */
  general?: boolean;
};

export const TOPICS: Topic[] = [
  { slug: "bewertung", label: "Immobilienbewertung", general: true },
  { slug: "hausverkauf", label: "Hausverkauf" },
  { slug: "wohnungsverkauf", label: "Wohnungsverkauf" },
  { slug: "marktwert", label: "Marktwert & Preisfindung" },
  { slug: "makler", label: "Immobilienmakler" },
  { slug: "erbschaft", label: "Erbschaft & Erbteilung" },
  { slug: "scheidung", label: "Scheidung & Trennung" },
  { slug: "leibrente", label: "Leibrente" },
  { slug: "teilverkauf", label: "Teilverkauf" },
  { slug: "mehrfamilienhaus", label: "Mehrfamilienhaus" },
  { slug: "grundstueck", label: "Grundstück & Bauland" },
  { slug: "renditeobjekt", label: "Renditeobjekt" },
];

export const TOPIC_MAP = new Map(TOPICS.map((t) => [t.slug, t]));

/** Lesbare Suchbegriffe auch für im Admin ergänzte Themen. */
export function topicLinkLabel(topic: string, focusKeyword = "", city = ""): string {
  const labels: Record<string, string> = {
    bewertung: "Immobilienbewertung",
    marktwert: "Marktwert Immobilie",
    "eigentumswohnung-wert": "Wert Eigentumswohnung",
    wohnungsmarktwert: "Wohnungsmarktwert",
    grundstueck: "Grundstück & Bauland bewerten",
    erbschaft: "Bewertung bei Erbschaft",
    scheidung: "Bewertung bei Scheidung",
  };
  const known = labels[topic] ?? TOPIC_MAP.get(topic)?.label;
  if (known) return known;
  const keyword = focusKeyword.trim();
  if (keyword && !keyword.includes("-")) {
    return city && keyword.toLocaleLowerCase("de-CH").endsWith(` ${city.toLocaleLowerCase("de-CH")}`)
      ? keyword.slice(0, -(city.length + 1)).trim()
      : keyword;
  }
  const readable = topic.replace(/-/g, " ").replace(/ae/g, "ä").replace(/oe/g, "ö").replace(/ue/g, "ü");
  return readable.charAt(0).toLocaleUpperCase("de-CH") + readable.slice(1);
}

export type District = {
  name: string;
  region: string;
  /** Zürcher Stadtquartiere brauchen andere Wappen-Muster */
  quarter?: boolean;
  places: string[];
};

export const DISTRICTS: District[] = [
  {
    name: "Bezirk Bülach",
    region: "Zürcher Unterland",
    places: [
      "Bachenbülach", "Bassersdorf", "Bülach", "Dietlikon", "Eglisau", "Embrach",
      "Freienstein-Teufen", "Glattfelden", "Hochfelden", "Höri", "Hüntwangen",
      "Kloten", "Lufingen", "Nürensdorf", "Oberembrach", "Opfikon", "Rafz",
      "Rorbas", "Wallisellen", "Wasterkingen", "Winkel",
    ],
  },
  {
    name: "Stadt Zürich",
    region: "Stadt Zürich",
    quarter: true,
    places: [
      "Zürich", "Altstadt", "Enge", "Wollishofen", "Leimbach", "Wiedikon",
      "Friesenberg", "Sihlfeld", "Aussersihl", "Langstrasse", "Hard",
      "Escher Wyss", "Unterstrass", "Oberstrass", "Fluntern", "Hottingen",
      "Hirslanden", "Witikon", "Seefeld", "Mühlebach", "Weinegg", "Albisrieden",
      "Altstetten", "Höngg", "Wipkingen", "Affoltern", "Oerlikon", "Seebach",
      "Schwamendingen",
    ],
  },
  {
    name: "Bezirk Dielsdorf",
    region: "Zürcher Unterland",
    places: [
      "Bachs", "Boppelsen", "Buchs", "Dällikon", "Dänikon", "Dielsdorf",
      "Hüttikon", "Neerach", "Niederglatt", "Niederhasli", "Niederweningen",
      "Oberglatt", "Oberweningen", "Otelfingen", "Regensberg", "Regensdorf",
      "Rümlang", "Schleinikon", "Schöfflisdorf", "Stadel", "Steinmaur", "Weiach",
    ],
  },
  {
    name: "Bezirk Winterthur",
    region: "Region Winterthur",
    places: [
      "Winterthur", "Altikon", "Brütten", "Dägerlen", "Dinhard", "Elsau",
      "Hettlingen", "Neftenbach", "Pfungen", "Rickenbach", "Schlatt", "Seuzach",
      "Turbenthal", "Wiesendangen", "Zell", "Kollbrunn",
    ],
  },
  {
    name: "Bezirk Uster",
    region: "Zürcher Oberland",
    places: [
      "Uster", "Dübendorf", "Egg", "Fällanden", "Greifensee", "Maur",
      "Mönchaltorf", "Schwerzenbach", "Volketswil", "Wangen-Brüttisellen",
    ],
  },
  {
    name: "Bezirk Meilen",
    region: "Zürichsee rechtes Ufer",
    places: [
      "Meilen", "Erlenbach", "Herrliberg", "Hombrechtikon", "Küsnacht",
      "Männedorf", "Oetwil am See", "Stäfa", "Uetikon am See", "Zollikon", "Zumikon",
    ],
  },
  {
    name: "Bezirk Horgen",
    region: "Zürichsee linkes Ufer",
    places: [
      "Horgen", "Adliswil", "Hirzel", "Kilchberg", "Langnau am Albis",
      "Oberrieden", "Richterswil", "Rüschlikon", "Thalwil", "Wädenswil",
    ],
  },
  {
    name: "Bezirk Dietikon",
    region: "Limmattal",
    places: [
      "Dietikon", "Aesch", "Birmensdorf", "Geroldswil", "Oberengstringen",
      "Oetwil an der Limmat", "Schlieren", "Uitikon", "Unterengstringen",
      "Urdorf", "Weiningen",
    ],
  },
  {
    name: "Bezirk Affoltern",
    region: "Knonauer Amt",
    places: [
      "Affoltern am Albis", "Aeugst am Albis", "Bonstetten", "Hausen am Albis",
      "Hedingen", "Kappel am Albis", "Knonau", "Maschwanden", "Mettmenstetten",
      "Obfelden", "Ottenbach", "Rifferswil", "Stallikon", "Wettswil am Albis",
    ],
  },
  {
    name: "Bezirk Hinwil",
    region: "Zürcher Oberland",
    places: [
      "Hinwil", "Bäretswil", "Bubikon", "Dürnten", "Fischenthal", "Gossau",
      "Grüningen", "Rüti", "Seegräben", "Wald", "Wetzikon",
    ],
  },
  {
    name: "Bezirk Pfäffikon",
    region: "Zürcher Oberland",
    places: [
      "Pfäffikon", "Bauma", "Fehraltorf", "Hittnau", "Illnau-Effretikon",
      "Kyburg", "Lindau", "Russikon", "Sternenberg", "Weisslingen", "Wila", "Wildberg",
    ],
  },
  {
    name: "Bezirk Andelfingen",
    region: "Zürcher Weinland",
    places: [
      "Andelfingen", "Adlikon", "Benken", "Berg am Irchel", "Buch am Irchel",
      "Dachsen", "Dorf", "Feuerthalen", "Flaach", "Henggart", "Kleinandelfingen",
      "Marthalen", "Ossingen", "Rheinau", "Stammheim", "Thalheim an der Thur",
      "Trüllikon", "Truttikon", "Volken",
    ],
  },
];

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Öffentlicher Pfad einer Regionalseite. */
export function pagePath(topic: string, citySlug: string): string {
  const t = TOPIC_MAP.get(topic);
  return t?.general ? `/standort/${citySlug}` : `/${topic}/${citySlug}`;
}

export function findPlace(citySlug: string) {
  for (const d of DISTRICTS) {
    const place = d.places.find((p) => slugify(p) === citySlug);
    if (place) return { place, district: d };
  }
  return null;
}

/** Statische Seiten der Hauptsite – Referenz für die Duplikatprüfung. */
export const STATIC_PAGES = [
  {
    path: "/",
    title: "Immobilienbewertung Zürich 2026 – kostenlos & unverbindlich",
    meta: "Kostenlose Immobilienbewertung für Eigentümer in Zürich: aktueller Marktwert 2026 in unter 30 Sekunden, basierend auf echten Verkaufsdaten aus Stadt und Kanton Zürich.",
  },
  {

    path: "/intern-admin",
    title: "Interner SEO/GEO-Admin",
    meta: "Interner Arbeitsbereich für regionale Landingpages.",
  },
];
